import type { ComponentProps, ReactNode } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly } from './shared'
import { Switch } from '../../ui/switch'
import { cn } from '@/lib/utils'
import { FieldLabelRow } from './field-label-row'

export default function SwitchField({
  label,
  description,
  className,
  size,
  variant,
  orientation,
  ...props
}: ComponentProps<'button'> & {
  label?: string
  description?: ReactNode
  size?: 'lg'
  variant?: 'primary'
  orientation?: 'vertical' | 'horizontal'
}) {
  const field = useFieldContext<boolean>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.disabled ?? formReadOnly
  const isHorizontal = orientation === 'horizontal'

  return (
    <div>
      <div className={cn('flex items-center gap-3', className)}>
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            // A switch always has a value (on/off), never show optional/required indicators.
            className={isHorizontal ? 'h-8 w-48 shrink-0' : undefined}
          />
        )}
        <Switch
          id={field.name}
          name={field.name}
          aria-invalid={hasFieldError(field.state.meta)}
          checked={field.state.value}
          onBlur={field.handleBlur}
          onCheckedChange={field.handleChange}
          size={size}
          variant={variant}
          {...props}
          disabled={isReadOnly}
          className={cn(isReadOnly && 'opacity-100')}
        />
        {description && <p className="text-muted-foreground text-xs leading-tight">{description}</p>}
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}
